import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../configuration/setup/store';
import { useAppSelector } from '../configuration/setup/hooks';
import { calcShowBodyBottomBar } from './data';
import { iamApi } from '../services/iamApi';

export type AppState = {
    sessionExpiredAcknowledged: boolean;
    showDeclineConfirmationDialog: boolean;
    showReAcceptConfirmationDialog: boolean;
    showChangesDetectedDialog: boolean;
    showActionButtons: boolean;
    showBodyBottomBarDueToHeight: boolean;
    previouslyDeclined: boolean;
};

const initialState: AppState = {
    sessionExpiredAcknowledged: false,
    showDeclineConfirmationDialog: false,
    showReAcceptConfirmationDialog: false,
    showChangesDetectedDialog: false,
    showActionButtons: false,
    showBodyBottomBarDueToHeight: calcShowBodyBottomBar(window.innerHeight),
    previouslyDeclined: false,
};

export const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        showDeclineConfirmationDialog: state => {
            state.showDeclineConfirmationDialog = true;
        },
        hideDeclineConfirmationDialog: state => {
            state.showDeclineConfirmationDialog = false;
        },
        showReAcceptConfirmationDialog: state => {
            state.showReAcceptConfirmationDialog = true;
        },
        hideReAcceptConfirmationDialog: state => {
            state.showReAcceptConfirmationDialog = false;
        },
        showChangesDetectedDialog: state => {
            state.showChangesDetectedDialog = true;
        },
        hideChangesDetectedDialog: state => {
            state.showChangesDetectedDialog = false;
        },
        hideSessionExpiredDialog: state => {
            state.sessionExpiredAcknowledged = true;
        },
        newInnerHeight: (state, action: PayloadAction<number>) => {
            state.showBodyBottomBarDueToHeight = calcShowBodyBottomBar(action.payload);
        },
        showActionButtons: state => {
            state.showActionButtons = true;
        },
        hideActionButtons: state => {
            state.showActionButtons = false;
        },
        setPreviouslyDeclined: (state, action: PayloadAction<boolean>) => {
            state.previouslyDeclined = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addMatcher(iamApi.endpoints.updateEulaStatus.matchFulfilled, state => {
            state.showDeclineConfirmationDialog = false;
            state.showActionButtons = false;
        });
    },
});

export const {
    hideSessionExpiredDialog,
    showDeclineConfirmationDialog,
    hideDeclineConfirmationDialog,
    showReAcceptConfirmationDialog,
    hideReAcceptConfirmationDialog,
    showChangesDetectedDialog,
    hideChangesDetectedDialog,
    newInnerHeight,
    showActionButtons,
    hideActionButtons,
    setPreviouslyDeclined,
} = appSlice.actions;

export const getSessionExpiredAcknowledged = (state: RootState) => state.app.sessionExpiredAcknowledged;
export const getShowDeclineConfirmationDialog = (state: RootState) => state.app.showDeclineConfirmationDialog;
export const getShowReAcceptConfirmationDialog = (state: RootState) => state.app.showReAcceptConfirmationDialog;
export const getShowChangedDetectedDialog = (state: RootState) => state.app.showChangesDetectedDialog;
export const getShowActionButtons = (state: RootState) => state.app.showActionButtons;
export const getShouldShowBodyBottomBarDueToHeight = (state: RootState) => state.app.showBodyBottomBarDueToHeight;
export const getPreviouslyDeclined = (state: RootState) => state.app.previouslyDeclined;

export const useSessionExpiredAcknowledged = () => useAppSelector(getSessionExpiredAcknowledged);
export const useShowDeclineConfirmationDialog = () => useAppSelector(getShowDeclineConfirmationDialog);
export const useShowReAcceptConfirmationDialog = () => useAppSelector(getShowReAcceptConfirmationDialog);
export const useShowChangesDetectedDialog = () => useAppSelector(getShowChangedDetectedDialog);
export const useShowActionButtons = () => useAppSelector(getShowActionButtons);
export const useShouldShowBodyBottomBarDueToHeight = () => useAppSelector(getShouldShowBodyBottomBarDueToHeight);
export const usePreviouslyDeclined = () => useAppSelector(getPreviouslyDeclined);

export default appSlice.reducer;
